/**
 * THangarUnitDoorEditor - Simplified door editor for T-hangar units.
 *
 * T-hangar units only have a single front door (on the wing-span edge).
 * Uses the shared DoorEditor component for the single door.
 */

import { Button } from '@/components/ui/button'
import { Copy, DoorOpen, Plus } from 'lucide-react'
import type { DoorSpec, DoorType, UnitDoorsConfig } from '@/lib/templates/types'
import { DoorEditor } from '../shared/DoorEditor'

interface THangarUnitDoorEditorProps {
  unitIndex: number
  unitCount: number
  doors: UnitDoorsConfig | undefined
  onChange: (doors: UnitDoorsConfig) => void
  /** Front wall width = unit_width_ft */
  unitWidthFt: number
  /** Max door height */
  unitHeightFt: number
  onCopyToAll?: () => void
}

export function THangarUnitDoorEditor({
    unitCount,
  doors,
  onChange,
  unitWidthFt,
  unitHeightFt,
  onCopyToAll,
}: THangarUnitDoorEditorProps) {
  const frontDoors = doors?.front || []

  const addDoor = () => {
    const newDoorWidth = Math.min(36, unitWidthFt - 4)
    const newDoor: DoorSpec = {
      offset_ft: (unitWidthFt - newDoorWidth) / 2,
      width_ft: newDoorWidth,
      height_ft: Math.min(14, unitHeightFt - 2),
      type: 'rolling' as DoorType,
    }
    onChange({ front: [...frontDoors, newDoor] })
  }

  const removeDoor = (index: number) => {
    onChange({ front: frontDoors.filter((_, i) => i !== index) })
  }

  const updateDoor = (index: number, updates: Partial<DoorSpec>) => {
    const updated = [...frontDoors]
    updated[index] = { ...updated[index], ...updates }
    onChange({ front: updated })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
          <DoorOpen className="h-4 w-4" />
          Front Door ({frontDoors.length})
        </h4>
        {onCopyToAll && unitCount > 1 && frontDoors.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onCopyToAll}
          >
            <Copy className="h-3 w-3 mr-1" />
            Apply to all units
          </Button>
        )}
      </div>

      {frontDoors.map((door, index) => (
        <DoorEditor
          key={index}
          door={door}
          index={index}
          wallLength={unitWidthFt}
          maxHeight={unitHeightFt}
          onUpdate={(updates) => updateDoor(index, updates)}
          onRemove={() => removeDoor(index)}
        />
      ))}

      {frontDoors.length === 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addDoor}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Front Door
        </Button>
      )}
    </div>
  )
}
